import React, { useState, useEffect } from 'react';
import {
  Rss, RefreshCw, ExternalLink, Trash2, Loader2
} from 'lucide-react';
import { useAdmin } from './AdminContext';

export default function NewsPage() {
  const { apiUrl, authHeaders } = useAdmin();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchArticles = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/blog/posts?published_only=false`);
      const data = await res.json();
      setArticles(data.filter(p => p.source === 'rss'));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchArticles(); }, []);

  const refreshNews = async () => {
    setRefreshing(true);
    try {
      await fetch(`${apiUrl}/api/news/fetch`, { method: 'POST' });
      await fetchArticles();
    } catch (err) {
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    await fetch(`${apiUrl}/api/blog/posts/${id}`, { method: 'DELETE', headers: authHeaders() });
    fetchArticles();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-[#4fd1c5] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-5xl" data-testid="admin-news-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Auto-fetched News</h2>
          <p className="text-gray-500 text-sm mt-0.5">{articles.length} articles from Google News RSS</p>
        </div>
        <button
          onClick={refreshNews}
          disabled={refreshing}
          data-testid="refresh-news-btn"
          className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] hover:border-[#4fd1c5]/30 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Fetching...' : 'Refresh Now'}
        </button>
      </div>

      {/* Feed info */}
      <div className="bg-[#4fd1c5]/5 border border-[#4fd1c5]/10 rounded-xl p-4 flex items-center gap-3">
        <Rss className="w-5 h-5 text-[#4fd1c5] flex-shrink-0" />
        <div>
          <p className="text-white text-xs font-medium">4 RSS feeds monitored</p>
          <p className="text-gray-400 text-[10px]">Auto-refresh every 6 hours | e-waste recycling, circular economy, IT disposal, sustainability</p>
        </div>
      </div>

      {/* Articles */}
      <div className="space-y-2">
        {articles.length === 0 ? (
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-12 text-center">
            <Rss className="w-8 h-8 text-gray-700 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No RSS articles yet. Click Refresh to fetch.</p>
          </div>
        ) : (
          articles.slice(0, 30).map(article => (
            <div
              key={article.id}
              className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 flex items-start gap-4 hover:border-white/[0.1] transition-colors"
              data-testid={`news-article-${article.id}`}
            >
              {article.image_url && (
                <img
                  src={article.image_url}
                  alt=""
                  className="w-16 h-16 rounded-lg object-cover flex-shrink-0 hidden sm:block"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-semibold text-[#4fd1c5] uppercase">{article.category?.split(':')[0]}</span>
                  <span className="text-gray-600 text-[10px]">{article.date}</span>
                </div>
                <h4 className="text-white text-sm font-medium truncate">{article.title}</h4>
                <p className="text-gray-500 text-xs mt-1 line-clamp-1">{article.excerpt}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {article.source_url && (
                  <a
                    href={article.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-500 hover:text-[#4fd1c5] transition-colors rounded-lg hover:bg-white/[0.04]"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <button
                  onClick={() => deleteArticle(article.id)}
                  className="p-2 text-gray-600 hover:text-red-400 transition-colors rounded-lg hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
